import { QuestionMarkCircleIcon } from "@heroicons/react/24/outline";
import {
    Dialog,
    DialogPanel,
    DialogTitle,
} from "lib/headlessui/components/dialog/dialog";
import {
    Transition,
    TransitionChild,
} from "lib/headlessui/components/transition/transition";
import React from "react";
import ReactDOM from "react-dom/client";
import { breakNewlines } from "util/text";
import { PrimaryButton } from "./primary-button";
import { SecondaryButton } from "./secondary-button";

export type FancyChoiceActionStyle = "primary" | "secondary" | "subtle";

export interface FancyChoiceAction<T extends string> {
    label: string;
    value: T;
    style: FancyChoiceActionStyle;
}

export interface FancyChoiceOptions<T extends string> {
    title: string;
    prompt: string;
    actions: FancyChoiceAction<T>[];
}

interface ChoiceDialogProps {
    title: string;
    prompt: string;
    actions: FancyChoiceAction<string>[];
    resolve: (value: string) => void;
}

const ChoiceDialog: React.FC<ChoiceDialogProps> = props => {
    const { title, prompt, actions, resolve } = props;
    const [isHidden, setIsHidden] = React.useState(false);

    const handleChoice = React.useCallback(
        (value: string) => {
            resolve(value);
            setIsHidden(true);
        },
        [resolve],
    );

    const primaryActions = actions.filter(a => a.style === "primary");
    const secondaryActions = actions.filter(a => a.style === "secondary");
    const subtleActions = actions.filter(a => a.style === "subtle");
    const gridColsClass: string =
        {
            1: "sm:grid-cols-1",
            2: "sm:grid-cols-2",
            3: "sm:grid-cols-3",
        }[primaryActions.length + secondaryActions.length] ?? "sm:grid-cols-2";

    return (
        <Transition show={!isHidden} appear={true}>
            <Dialog as="div" className="relative z-1500" onClose={close}>
                <TransitionChild
                    enter="ease-out duration-300"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in duration-200"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0">
                    <div className="fixed inset-0 bg-gray-500/75 transition-opacity" />
                </TransitionChild>

                <div className="fixed z-1500 inset-0 overflow-y-auto">
                    <div className="flex items-center justify-center min-h-full p-4 text-center sm:p-0">
                        <TransitionChild
                            enter="ease-out duration-300"
                            enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                            enterTo="opacity-100 translate-y-0 sm:scale-100"
                            leave="ease-in duration-200"
                            leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                            leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95">
                            <DialogPanel className="relative bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:max-w-lg sm:w-full sm:p-6">
                                <div className="sm:flex sm:items-start">
                                    <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-brand-100 sm:mx-0 sm:h-10 sm:w-10">
                                        <QuestionMarkCircleIcon
                                            className="h-6 w-6 text-brand-600"
                                            aria-hidden="true"
                                        />
                                    </div>
                                    <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                                        <DialogTitle
                                            as="h3"
                                            className="text-lg leading-6 font-medium text-gray-900">
                                            {title}
                                        </DialogTitle>
                                        <div className="mt-2">
                                            <p className="text-sm text-gray-500">
                                                {breakNewlines(prompt)}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                                <div
                                    className={`mt-5 sm:mt-6 flex flex-col gap-3 sm:grid sm:gap-3 sm:grid-flow-row-dense ${gridColsClass}`}>
                                    {primaryActions.map(action => (
                                        <PrimaryButton
                                            key={action.value}
                                            onClick={() => handleChoice(action.value)}>
                                            {action.label}
                                        </PrimaryButton>
                                    ))}
                                    {secondaryActions.map(action => (
                                        <SecondaryButton
                                            key={action.value}
                                            onClick={() => handleChoice(action.value)}>
                                            {action.label}
                                        </SecondaryButton>
                                    ))}
                                </div>
                                {subtleActions.length > 0 ? (
                                    <div className="mt-2 flex justify-end gap-4">
                                        {subtleActions.map(action => (
                                            <button
                                                key={action.value}
                                                type="button"
                                                className="text-xs text-gray-400 hover:text-gray-600 underline"
                                                onClick={() =>
                                                    handleChoice(action.value)
                                                }>
                                                {action.label}
                                            </button>
                                        ))}
                                    </div>
                                ) : null}
                            </DialogPanel>
                        </TransitionChild>
                    </div>
                </div>
            </Dialog>
        </Transition>
    );
};

export async function fancyChoice<T extends string>(
    options: FancyChoiceOptions<T>,
): Promise<T> {
    const wrapper = document.body.appendChild(document.createElement("div"));
    const root = ReactDOM.createRoot(wrapper);
    const promise = new Promise<string>(resolve => {
        root.render(
            <ChoiceDialog
                title={options.title}
                prompt={options.prompt}
                actions={options.actions}
                resolve={resolve}
            />,
        );
    });
    const result = await promise;
    root.unmount();
    setTimeout(() => wrapper.remove());
    return result as T;
}
